'use client';

/**
 * ContentLoader Component
 *
 * Stripe-inspired content loading patterns for common page layouts.
 * Provides skeleton loaders that match the shape of the content being loaded.
 *
 * Usage:
 * ```tsx
 * // Table loading
 * <ContentLoader layout="table" rows={5} />
 *
 * // Card grid loading
 * <ContentLoader layout="cards" count={6} />
 *
 * // Dashboard with stats
 * <ContentLoader layout="dashboard" />
 *
 * // Form loading
 * <ContentLoader layout="form" fields={4} />
 * ```
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import type React from 'react';
import { ShimmerLoader } from '../ShimmerLoader/ShimmerLoader';

// ============================================================================
// Types
// ============================================================================

export type ContentLoaderLayout = 'table' | 'cards' | 'dashboard' | 'form' | 'detail' | 'list';

export interface ContentLoaderProps {
  /** Layout type */
  layout: ContentLoaderLayout;

  /** Number of rows for table layout */
  rows?: number;

  /** Number of cards for cards layout */
  count?: number;

  /** Number of fields for form layout */
  fields?: number;

  /** Number of columns for cards */
  columns?: number;

  /** Additional className */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled(Box)({
  width: '100%',
});

const TableHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TableRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
}));

const FormField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const DetailSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

// ============================================================================
// Layout Components
// ============================================================================

const TableLoader: React.FC<{ rows: number }> = ({ rows }) => (
  <Container>
    <TableHeader>
      <ShimmerLoader variant="text" width="15%" height={16} />
      <ShimmerLoader variant="text" width="20%" height={16} />
      <ShimmerLoader variant="text" width="25%" height={16} />
      <ShimmerLoader variant="text" width="15%" height={16} />
      <ShimmerLoader variant="text" width="10%" height={16} />
    </TableHeader>
    {Array.from({ length: rows }).map((_, index) => (
      <TableRow key={index}>
        <ShimmerLoader variant="text" width="15%" height={20} />
        <ShimmerLoader variant="text" width="20%" height={20} />
        <ShimmerLoader variant="text" width="25%" height={20} />
        <Box sx={{ width: '15%' }}>
          <ShimmerLoader variant="rounded" width={80} height={24} />
        </Box>
        <Box sx={{ width: '10%', display: 'flex', gap: 1 }}>
          <ShimmerLoader variant="circular" width={32} height={32} />
          <ShimmerLoader variant="circular" width={32} height={32} />
        </Box>
      </TableRow>
    ))}
  </Container>
);

const CardsLoader: React.FC<{ count: number; columns: number }> = ({ count, columns }) => (
  <CardGrid
    sx={{
      gridTemplateColumns: {
        xs: '1fr',
        sm: `repeat(${Math.min(columns, 2)}, 1fr)`,
        md: `repeat(${Math.min(columns, 3)}, 1fr)`,
        lg: `repeat(${columns}, 1fr)`,
      },
    }}
  >
    {Array.from({ length: count }).map((_, index) => (
      <ShimmerLoader key={index} variant="card" height={180} />
    ))}
  </CardGrid>
);

const DashboardLoader: React.FC = () => (
  <Container>
    {/* Stats row */}
    <CardGrid
      sx={{
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        mb: 4,
      }}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <ShimmerLoader variant="text" width="40%" height={14} />
          <Box sx={{ mt: 1 }}>
            <ShimmerLoader variant="text" width="60%" height={32} />
          </Box>
          <Box sx={{ mt: 1 }}>
            <ShimmerLoader variant="text" width="80%" height={12} />
          </Box>
        </Box>
      ))}
    </CardGrid>

    {/* Chart placeholder */}
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 4 }}>
      <ShimmerLoader variant="text" width="30%" height={20} />
      <Box sx={{ mt: 2 }}>
        <ShimmerLoader variant="rectangular" width="100%" height={250} />
      </Box>
    </Box>

    {/* Table section */}
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <ShimmerLoader variant="text" width="25%" height={20} />
      <Box sx={{ mt: 2 }}>
        <TableLoader rows={5} />
      </Box>
    </Box>
  </Container>
);

const FormLoader: React.FC<{ fields: number }> = ({ fields }) => (
  <Container>
    {Array.from({ length: fields }).map((_, index) => (
      <FormField key={index}>
        <ShimmerLoader variant="text" width="30%" height={14} />
        <ShimmerLoader variant="rounded" width="100%" height={44} />
      </FormField>
    ))}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
      <ShimmerLoader variant="button" width={100} height={40} />
      <ShimmerLoader variant="button" width={120} height={40} />
    </Box>
  </Container>
);

const DetailLoader: React.FC = () => (
  <Container>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
      <ShimmerLoader variant="circular" width={64} height={64} />
      <Box sx={{ flex: 1 }}>
        <ShimmerLoader variant="text" width="40%" height={28} />
        <ShimmerLoader variant="text" width="25%" height={16} />
      </Box>
      <ShimmerLoader variant="button" width={120} height={40} />
    </Box>

    {/* Sections */}
    {Array.from({ length: 3 }).map((_, sectionIndex) => (
      <DetailSection key={sectionIndex}>
        <ShimmerLoader variant="text" width="20%" height={18} />
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
          {Array.from({ length: 4 }).map((_, fieldIndex) => (
            <Box key={fieldIndex}>
              <ShimmerLoader variant="text" width="40%" height={12} />
              <ShimmerLoader variant="text" width="70%" height={18} />
            </Box>
          ))}
        </Box>
      </DetailSection>
    ))}
  </Container>
);

const ListLoader: React.FC<{ rows: number }> = ({ rows }) => (
  <Container>
    {Array.from({ length: rows }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ShimmerLoader variant="circular" width={48} height={48} />
        <Box sx={{ flex: 1 }}>
          <ShimmerLoader variant="text" width="60%" height={18} />
          <ShimmerLoader variant="text" width="40%" height={14} />
        </Box>
        <ShimmerLoader variant="rounded" width={80} height={28} />
      </Box>
    ))}
  </Container>
);

// ============================================================================
// Main Component
// ============================================================================

export const ContentLoader: React.FC<ContentLoaderProps> = ({
  layout,
  rows = 5,
  count = 6,
  fields = 4,
  columns = 3,
  className,
}) => {
  const theme = useTheme();

  const renderLayout = () => {
    switch (layout) {
      case 'table':
        return <TableLoader rows={rows} />;
      case 'cards':
        return <CardsLoader count={count} columns={columns} />;
      case 'dashboard':
        return <DashboardLoader />;
      case 'form':
        return <FormLoader fields={fields} />;
      case 'detail':
        return <DetailLoader />;
      case 'list':
        return <ListLoader rows={rows} />;
      default:
        return <TableLoader rows={rows} />;
    }
  };

  return (
    <Box className={className} sx={{ color: theme.palette.text.primary }}>
      {renderLayout()}
    </Box>
  );
};

export default ContentLoader;
